import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Renders a small "you may also like" upsell list inside the cart drawer,
 * based on the most recently added product, and lets the shopper add a
 * recommended product straight from the drawer.
 */
class CartDrawerRecommendations extends Component {
  /** @type {any[]} */
  #cachedProducts = [];

  /** @type {any[]|null} */
  #cachedFallbackProducts = null;

  /** @type {MutationObserver} */
  #selfHealObserver = new MutationObserver(() => this.#reconcile());

  connectedCallback() {
    super.connectedCallback();
    this.#load();
    this.#selfHealObserver.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#selfHealObserver.disconnect();
  }

  /**
   * The Section Rendering API (and other background hydration/prefetch passes
   * in this theme) can re-render or reset this element's markup independently
   * of our own code, sometimes after our fetch has already populated the list.
   * Rather than chase every possible timing race, we keep the last successful
   * result and self-heal: whenever the list is found empty again but we have
   * cached products, we re-render from cache (no network call) instead of
   * leaving the upsell blank.
   */
  #reconcile() {
    if (this.#cachedProducts.length === 0) return;
    const list = this.querySelector('[data-recommendations-list]');
    if (!list || list.children.length > 0) return;
    this.#render(this.#cachedProducts, list);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#load();
  }

  async #load() {
    const { productId, url, limit, cartProductIds } = this.dataset;
    if (!productId || !url) return;

    const max = Number(limit) || 4;
    const excluded = new Set((cartProductIds || '').split(',').filter(Boolean));
    excluded.add(String(productId));

    try {
      let data = await this.#fetchRecommendations(url, productId, max, 'complementary');
      if (!data.products || data.products.length === 0) {
        data = await this.#fetchRecommendations(url, productId, max, 'related');
      }

      let products = (data.products || []).filter((p) => !excluded.has(String(p.id)));

      // Real recommendations can come back thin (or empty) for stores that
      // don't yet have enough sales/view history to power them well. Rather
      // than leave the upsell column sparse, top it up with other available
      // products from the store -- still excluding anything already in the
      // cart or already picked above -- so the panel stays filled.
      if (products.length < max) {
        const alreadyPicked = new Set(products.map((p) => String(p.id)));
        const fallback = this.#getFallbackProducts().filter(
          (p) => !excluded.has(String(p.id)) && !alreadyPicked.has(String(p.id))
        );
        products = products.concat(fallback);
      }

      products = products.slice(0, max);
      this.#cachedProducts = products;

      const list = this.querySelector('[data-recommendations-list]');
      if (!list) return;

      if (products.length === 0) {
        this.classList.add('hidden');
        return;
      }

      this.#render(products, list);
    } catch (error) {
      console.error('Cart drawer recommendations error:', error);
      this.classList.add('hidden');
    }
  }

  /**
   * Reads the plain-JSON catalog fallback list rendered server-side by the
   * Liquid snippet (a `<script type="application/json" data-fallback-products>`
   * tag inside this element), used to top up the recommendation list when the
   * real recommendations endpoint doesn't return enough products.
   *
   * @returns {any[]}
   */
  #getFallbackProducts() {
    if (this.#cachedFallbackProducts) return this.#cachedFallbackProducts;
    const script = this.querySelector('[data-fallback-products]');
    try {
      this.#cachedFallbackProducts = script ? JSON.parse(script.textContent || '[]') : [];
    } catch (error) {
      console.error('Cart drawer recommendations: invalid fallback products JSON:', error);
      this.#cachedFallbackProducts = [];
    }
    return this.#cachedFallbackProducts;
  }

  #render(products, list) {
    this.classList.remove('hidden');
    list.innerHTML = products.map((p) => this.#renderCard(p)).join('');
    this.#bindAddButtons(list);
  }

  /**
   * @param {string} baseUrl
   * @param {string} productId
   * @param {number} limit
   * @param {'related'|'complementary'} intent
   */
  async #fetchRecommendations(baseUrl, productId, limit, intent) {
    const url = `${baseUrl}?product_id=${encodeURIComponent(productId)}&limit=${limit}&intent=${intent}`;
    const response = await fetch(url);
    if (!response.ok) return { products: [] };
    return response.json();
  }

  #renderCard(product) {
    const variant = product.variants && product.variants[0];
    const variantId = variant ? variant.id : product.id;
    const image = product.featured_image || (product.images && product.images[0]) || '';
    const price = this.#formatMoney(product.price);
    const title = this.#escape(product.title);

    return `
      <div class="cart-drawer-recommendations__card">
        <a href="${product.url}" class="cart-drawer-recommendations__image" tabindex="-1">
          ${image ? `<img src="${image}" alt="" loading="lazy" width="120" height="120">` : ''}
        </a>
        <div class="cart-drawer-recommendations__info">
          <a href="${product.url}" class="cart-drawer-recommendations__title">${title}</a>
          <span class="cart-drawer-recommendations__price">${price}</span>
        </div>
        <button
          type="button"
          class="cart-drawer-recommendations__add"
          data-variant-id="${variantId}"
          aria-label="${title} in den Warenkorb legen"
        >
          <span>In den Warenkorb</span>
        </button>
      </div>
    `;
  }

  #escape(str) {
    const div = document.createElement('div');
    div.textContent = str ?? '';
    return div.innerHTML;
  }

  #formatMoney(cents) {
    const currency = this.dataset.currency || 'EUR';
    try {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format((cents || 0) / 100);
    } catch {
      return `${((cents || 0) / 100).toFixed(2)} €`;
    }
  }

  #bindAddButtons(list) {
    list.querySelectorAll('.cart-drawer-recommendations__add').forEach((btn) => {
      btn.addEventListener('click', () => this.#addToCart(btn));
    });
  }

  /**
   * Adds a recommended product to the cart and refreshes the drawer.
   *
   * This deliberately does NOT call morphSection itself. A manual
   * `fetch('/cart?section_id=...')` + `morphSection(..., {mode:'hydration'})` was tried
   * first, and while it reported success it never visibly updated the drawer -- most
   * likely because a plain fetch doesn't carry whatever section-rendering context the
   * server needs to emit the `data-hydration-key` markup hydration-mode morphs rely on.
   * Instead this replicates the theme's own native add-to-cart flow (see
   * ProductFormComponent#processAddToCart in product-form.js): dispatch a
   * CartLinesUpdateEvent with a deferred promise, POST to /cart/add.js requesting
   * `sections` HTML for every on-page `cart-items-component`, then resolve the promise
   * with that sections HTML. The existing `cart-items-component` already listens for
   * this event on `document` and morphs itself from `detail.sections` -- so the drawer
   * (and any other cart UI, e.g. a header cart count) refreshes exactly the way it does
   * for a normal product-page add-to-cart.
   *
   * @param {HTMLButtonElement} btn
   */
  async #addToCart(btn) {
    if (btn.disabled) return;
    btn.disabled = true;
    btn.classList.add('is-loading');

    const variantId = btn.dataset.variantId;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .filter((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
        }),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.message || `Add to cart failed with status ${response.status}`);
      }

      const cartUrl = this.dataset.cartUrl || '/cart';
      const cartResponse = await fetch(`${cartUrl}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const ajaxCart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'cart-drawer-recommendations',
          itemCount: 1,
          sections: data.sections,
          didError: false,
        },
      });

      btn.classList.remove('is-loading');
      btn.classList.add('is-added');

      // Drop the just-added card from the list once the drawer has visibly updated.
      // The server-rendered `data-cart-product-ids` on this element would eventually
      // exclude it too, but a hydration-mode morph doesn't reliably refresh that
      // dataset attribute, so we also update client-side state to stay in sync.
      setTimeout(() => {
        const card = btn.closest('.cart-drawer-recommendations__card');
        this.#cachedProducts = this.#cachedProducts.filter((p) => {
          const v = p.variants && p.variants[0];
          return String(v ? v.id : p.id) !== String(variantId);
        });
        card?.remove();
        if (this.#cachedProducts.length === 0) this.classList.add('hidden');
      }, 600);
    } catch (error) {
      console.error('Cart drawer recommendations: add to cart error:', error);
      deferred.reject(error);
      btn.classList.remove('is-loading');
    } finally {
      btn.disabled = false;
    }
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
